import { useT } from "@transifex/react";
import BasePopupWrapper, { TitleColorVariants } from "../../../basic/popups/BasePopupWrapper";
import { copyTextToClipboard } from "../../../helpers/clipboardHelpers";
import { getClassMemberDisplayUrl, getClassMemberUrl } from "../../../helpers/schoolClassHelpers";
import useQrCodeDataUrl from "../../../hooks/useQrCodeDataUrl";
import type { ClassMember } from "../../../networking/schoolClasses";
import useBreakpoints from "../../../hooks/useBreakpoints";

export enum ClassMemberCopyKind {
    CLASS_CODE = "CLASS_CODE",
    PERSONAL_CODE = "PERSONAL_CODE",
    DIRECT_LINK = "DIRECT_LINK",
}

interface ShareClassMemberAccessPopupProps {
    isOpen: boolean;
    onClose: () => void;
    schoolClassId: string;
    classMember: ClassMember;
    onCopied: (kind: ClassMemberCopyKind) => void;
    onCopyFailed: () => void;
}

const ShareClassMemberAccessPopup = (props: ShareClassMemberAccessPopupProps) => {
    const t = useT();
    const { isAboveSM } = useBreakpoints();

    const classCode = props.schoolClassId.toUpperCase();
    const personalCode = props.classMember.loginCode.toUpperCase();
    const directLink = getClassMemberUrl(props.schoolClassId, props.classMember.loginCode);
    const directLinkDisplay = getClassMemberDisplayUrl(props.schoolClassId, props.classMember.loginCode);
    const qrDataUrl = useQrCodeDataUrl(props.isOpen ? directLink : "", 160);

    const t_title = t("Access for {name}", {
        _tags: "SchoolClasses, ShareClassMemberAccessPopup",
        name: props.classMember.name,
    });
    const t_classroom = t("Classroom:", { _tags: "SchoolClasses, ShareClassMemberAccessPopup" });
    const t_personalCode = t("Personal code:", { _tags: "SchoolClasses, ShareClassMemberAccessPopup" });
    const t_directLink = t("Direct link:", { _tags: "SchoolClasses, ShareClassMemberAccessPopup" });
    const t_copyDirectLink = t("Copy direct link", { _tags: "SchoolClasses, ShareClassMemberAccessPopup" });
    const t_close = t("Close", { _tags: "SchoolClasses, ShareClassMemberAccessPopup" });
    const t_qrCode = t("QR Code", { _tags: "SchoolClasses, ShareClassMemberAccessPopup" });

    const handleCopy = (value: string, kind: ClassMemberCopyKind) => {
        copyTextToClipboard(value).then((copied) => (copied ? props.onCopied(kind) : props.onCopyFailed()));
    };

    /** Codes are copied as shown; only the link differs, where the display value drops the scheme. */
    const renderCopyRow = (
        label: string,
        displayValue: string,
        copyValue: string,
        kind: ClassMemberCopyKind,
        testid: string
    ) => (
        <button
            type="button"
            data-testid={testid}
            onClick={() => handleCopy(copyValue, kind)}
            className="bg-transparent border-none p-0 cursor-pointer text-left text-s text-p6-grey-dark hover:text-p6-grey-darkest"
        >
            <span className="font-semibold">{label}</span> <span className="break-all">{displayValue}</span>
        </button>
    );

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={props.onClose}
            title={t_title}
            titleColor={TitleColorVariants.DEFAULT}
            maxWidth={448}
            secondaryButton={{
                text: t_close,
                onClick: props.onClose,
                className: "flex-1",
            }}
            primaryButton={{
                text: t_copyDirectLink,
                onClick: () => handleCopy(directLink, ClassMemberCopyKind.DIRECT_LINK),
                className: "flex-1",
            }}
        >
            <div className={`flex items-center gap-xs w-full ${isAboveSM ? "flex-row" : "flex-col"}`}>
                {qrDataUrl && (
                    <img
                        data-testid="class-member-access-qr"
                        src={qrDataUrl}
                        alt={t_qrCode}
                        className="w-[160px] max-w-full h-auto shrink-0"
                    />
                )}
                <div className="flex flex-col gap-xs min-w-0">
                    {renderCopyRow(
                        t_classroom,
                        classCode,
                        classCode,
                        ClassMemberCopyKind.CLASS_CODE,
                        "class-member-access-class-code"
                    )}
                    {renderCopyRow(
                        t_personalCode,
                        personalCode,
                        personalCode,
                        ClassMemberCopyKind.PERSONAL_CODE,
                        "class-member-access-personal-code"
                    )}
                    {renderCopyRow(
                        t_directLink,
                        directLinkDisplay,
                        directLink,
                        ClassMemberCopyKind.DIRECT_LINK,
                        "class-member-access-link"
                    )}
                </div>
            </div>
        </BasePopupWrapper>
    );
};

export default ShareClassMemberAccessPopup;
